import React, { Component } from 'react';
import { connect } from 'react-redux';
import cx from 'classnames';
import withStyles from 'isomorphic-style-loader/lib/withStyles';
import { Collapse } from 'react-bootstrap';
import { FormattedMessage, injectIntl } from 'react-intl';

// Lucide Icons
import {
  LayoutDashboard,
  Ship,
  Sparkles,
  CalendarCheck,
  Shield,
  Users,
  UserCog,
  Star,
  MessageSquare,
  Flag,
  Wallet,
  CreditCard,
  DollarSign,
  Search,
  Globe,
  KeyRound,
  FileText,
  Home,
  Settings,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  PanelLeftClose,
  PanelLeft,
  LogOut,
  ExternalLink,
  Languages,
  Palette,
  Mail,
  MapPin,
  Image,
  BookOpen,
  Sliders,
  X,
  Smartphone,
  Share2,
  BarChart3
} from 'lucide-react';

// Components
import Link from '../../Link';
import Logo from '../../Logo';

// Helpers & Actions
import history from '../../../core/history';
import messages from '../../../locale/messages';
import { adminLogout } from '../../../actions/siteadmin/logout';
import { openHeaderModal } from '../../../actions/modalActions';
import { formatLocale } from '../../../helpers/formatLocale';
import { validatePrivilege } from '../../../helpers/adminPrivileges';

import s from './SideBar.css';

class SideBar extends Component {
  static defaultProps = {
    isSuperAdmin: false,
    privileges: []
  };

  constructor(props) {
    super(props);
    this.state = {
      location: '',
      isCollapsed: false,
      isOpen: 0,
      // Submenu states
      expandedMenus: {}
    };
    this.sidebarNavRef = React.createRef();
  }

  componentDidMount() {
    this.setState({ location: history.location.pathname });
  }

  // Save and restore scroll position to prevent jump
  saveScrollPosition = () => {
    if (this.sidebarNavRef.current) {
      this.savedScrollTop = this.sidebarNavRef.current.scrollTop;
    }
  };

  restoreScrollPosition = () => {
    if (this.sidebarNavRef.current && this.savedScrollTop !== undefined) {
      requestAnimationFrame(() => {
        if (this.sidebarNavRef.current) {
          this.sidebarNavRef.current.scrollTop = this.savedScrollTop;
        }
      });
    }
  };

  componentDidUpdate(prevProps, prevState) {
    const { location } = this.props;
    if (prevState.location !== location) {
      this.setState({ location });
    }
  }

  toggleCollapse = () => {
    this.setState(prev => ({ isCollapsed: !prev.isCollapsed }));
  };

  toggleSubmenu = (menuKey) => {
    this.setState(prev => ({
      expandedMenus: {
        ...prev.expandedMenus,
        [menuKey]: !prev.expandedMenus[menuKey]
      }
    }));
  };

  openMenu = () => this.setState({ isOpen: 1 });
  closeMenu = () => this.setState({ isOpen: 0 });

  // Handle navigation click - prevent scroll jump
  handleNavClick = (e) => {
    // Save current scroll position of sidebar
    if (this.sidebarNavRef.current) {
      const scrollTop = this.sidebarNavRef.current.scrollTop;
      // Restore after navigation
      setTimeout(() => {
        if (this.sidebarNavRef.current) {
          this.sidebarNavRef.current.scrollTop = scrollTop;
        }
      }, 0);
    }
    // Only close menu on mobile
    if (this.state.isOpen === 1) {
      this.setState({ isOpen: 0 });
    }
  };

  // Check if current path matches
  isActive = (path) => {
    const { location } = this.state;
    if (Array.isArray(path)) {
      return path.some(p => location === p || location.startsWith(p));
    }
    return location === path || location.startsWith(path);
  };

  // Render a single menu item
  renderMenuItem = (icon, label, path, privilegeId = null) => {
    const { isSuperAdmin, privileges } = this.props;
    const { isCollapsed } = this.state;
    const Icon = icon;

    // Check privileges
    if (privilegeId && !validatePrivilege(privilegeId, privileges) && !isSuperAdmin) {
      return null;
    }

    return (
      <li key={path} className={cx({ [s.active]: this.isActive(path) })}>
        <Link to={path} className={s.sideNavitem} onClick={this.handleNavClick}>
          <Icon className={s.sideNavIcon} size={20} />
          <span className={s.menuText}>{label}</span>
        </Link>
      </li>
    );
  };

  // Render submenu with collapsible items
  renderSubmenu = (icon, label, menuKey, items, activeCheck) => {
    const { isSuperAdmin, privileges } = this.props;
    const { expandedMenus, isCollapsed } = this.state;
    const Icon = icon;
    const isExpanded = expandedMenus[menuKey];
    const hasActiveChild = activeCheck;

    // Filter items based on privileges
    const visibleItems = items.filter(item => {
      if (item.privilegeId) {
        return validatePrivilege(item.privilegeId, privileges) || isSuperAdmin;
      }
      if (item.superAdminOnly) {
        return isSuperAdmin;
      }
      return true;
    });

    if (visibleItems.length === 0) return null;

    return (
      <li className={cx({ [s.active]: hasActiveChild && !isExpanded })}>
        <button
          className={cx(s.sideNavitem, { [s.menuOpen]: isExpanded })}
          onClick={() => this.toggleSubmenu(menuKey)}
        >
          <Icon className={s.sideNavIcon} size={20} />
          <span className={s.menuText}>{label}</span>
          {!isCollapsed && (
            <ChevronDown className={s.navigationIcon} size={16} />
          )}
        </button>
        <Collapse in={isExpanded && !isCollapsed}>
          <ul className={s.subMenu}>
            {visibleItems.map((item, idx) => (
              <li key={idx} className={cx({ [s.active]: this.isActive(item.path) })}>
                <Link to={item.path} className={s.sideNavitem} onClick={this.handleNavClick}>
                  <span className={s.menuText}>{item.label}</span>
                </Link>
              </li>
            ))}
          </ul>
        </Collapse>
      </li>
    );
  };

  render() {
    const { adminLogout, openHeaderModal, currentLocale, isSuperAdmin, privileges } = this.props;
    const { formatMessage } = this.props.intl;
    const { isCollapsed, isOpen, location } = this.state;

    // Define menu groups
    const menuGroups = [
      {
        title: 'GENERAL',
        items: [
          { type: 'item', icon: LayoutDashboard, label: formatMessage(messages.dashboard), path: '/siteadmin' },
          { type: 'item', icon: Settings, label: formatMessage(messages.siteSettings), path: '/siteadmin/settings/site', privilegeId: 1 },
          { type: 'item', icon: Sliders, label: formatMessage(messages.manageSiteConfig), path: '/siteadmin/settings/config', superAdminOnly: true },
        ]
      },
      {
        title: 'FLOTA',
        items: [
          { type: 'item', icon: Ship, label: formatMessage(messages.manageListing), path: '/siteadmin/listings', privilegeId: 3 },
          { type: 'item', icon: Sparkles, label: formatMessage(messages.featuredSettings), path: '/siteadmin/featured-settings', superAdminOnly: true },
          {
            type: 'submenu',
            icon: Sliders,
            label: formatMessage(messages.carSettingsForStep) + ' 1',
            menuKey: 'step1',
            activeCheck: ['/siteadmin/listsettings/1', '/siteadmin/listsettings/20', '/siteadmin/listsettings/3', '/siteadmin/listsettings/4', '/siteadmin/listsettings/10', '/siteadmin/listsettings/21'].some(p => this.isActive(p)),
            privilegeId: 17,
            items: [
              { label: formatMessage(messages.carType), path: '/siteadmin/listsettings/1' },
              { label: formatMessage(messages.makeLabel), path: '/siteadmin/listsettings/20' },
              { label: formatMessage(messages.modelLabel), path: '/siteadmin/listsettings/3' },
              { label: formatMessage(messages.year), path: '/siteadmin/listsettings/4' },
              { label: formatMessage(messages.carFeatures), path: '/siteadmin/listsettings/10' },
              { label: formatMessage(messages.odometer), path: '/siteadmin/listsettings/21' },
            ]
          },
          {
            type: 'submenu',
            icon: BookOpen,
            label: formatMessage(messages.carSettingsForStep) + ' 3',
            menuKey: 'step3',
            activeCheck: ['/siteadmin/listsettings/13', '/siteadmin/listsettings/14', '/siteadmin/listsettings/15', '/siteadmin/listsettings/18', '/siteadmin/listsettings/19'].some(p => this.isActive(p)),
            privilegeId: 17,
            items: [
              { label: formatMessage(messages.guestRequirements), path: '/siteadmin/listsettings/13' },
              { label: formatMessage(messages.carRules), path: '/siteadmin/listsettings/14' },
              { label: formatMessage(messages.reviewHowRentersBook), path: '/siteadmin/listsettings/15' },
              { label: formatMessage(messages.minimumDays), path: '/siteadmin/listsettings/18' },
              { label: formatMessage(messages.maximumDays), path: '/siteadmin/listsettings/19' },
            ]
          },
        ]
      },
      {
        title: 'RESERVAS',
        items: [
          { type: 'item', icon: CalendarCheck, label: formatMessage(messages.manageReservations), path: '/siteadmin/reservations', privilegeId: 4 },
          { type: 'item', icon: Shield, label: formatMessage(messages.manageSecurityDeposit), path: '/siteadmin/manage-security-deposit', privilegeId: 20 },
        ]
      },
      {
        title: 'USUARIOS',
        items: [
          { type: 'item', icon: Users, label: formatMessage(messages.manageUser), path: '/siteadmin/users', privilegeId: 2 },
          {
            type: 'submenu',
            icon: UserCog,
            label: formatMessage(messages.manageAdmin),
            menuKey: 'admins',
            activeCheck: this.isActive(['/siteadmin/admin-users', '/siteadmin/admin-roles']),
            superAdminOnly: true,
            items: [
              { label: formatMessage(messages.manageAdminUsers), path: '/siteadmin/admin-users' },
              { label: formatMessage(messages.manageAdminRoles), path: '/siteadmin/admin-roles' },
            ]
          },
          { type: 'item', icon: FileText, label: formatMessage(messages.documentverificaiton), path: '/siteadmin/document', privilegeId: 8 },
          { type: 'item', icon: MessageSquare, label: 'User Feedback', path: '/siteadmin/feedback' },
          { type: 'item', icon: Flag, label: formatMessage(messages.reportManagement), path: '/siteadmin/reportUser', privilegeId: 10 },
        ]
      },
      {
        title: 'REVIEWS',
        items: [
          { type: 'item', icon: Star, label: formatMessage(messages.reviewManagement), path: '/siteadmin/user-reviews', privilegeId: 5 },
          {
            type: 'submenu',
            icon: Star,
            label: formatMessage(messages.adminReviews),
            menuKey: 'adminReviews',
            activeCheck: this.isActive(['/siteadmin/reviews', '/siteadmin/write-reviews', '/siteadmin/edit-review/']),
            privilegeId: 6,
            items: [
              { label: formatMessage(messages.manageReviwes), path: '/siteadmin/reviews' },
              { label: formatMessage(messages.writeReviwes), path: '/siteadmin/write-reviews' },
            ]
          },
        ]
      },
      {
        title: 'FINANZAS',
        items: [
          { type: 'item', icon: DollarSign, label: formatMessage(messages.manageServiceFee), path: '/siteadmin/settings/servicefees', privilegeId: 7 },
          { type: 'item', icon: Wallet, label: formatMessage(messages.payOutManagement), path: '/siteadmin/payout', privilegeId: 11 },
          { type: 'item', icon: CreditCard, label: 'Megasoft Payouts', path: '/siteadmin/payout-megasoft', privilegeId: 11 },
          { type: 'item', icon: CreditCard, label: formatMessage(messages.managePaymentGateWay), path: '/siteadmin/payment-gateway-section', privilegeId: 12 },
          { type: 'item', icon: Globe, label: formatMessage(messages.manageCurrency), path: '/siteadmin/currency', superAdminOnly: true },
        ]
      },
      {
        title: 'COMUNICACIÓN',
        items: [
          { type: 'item', icon: MessageSquare, label: formatMessage(messages.messages), path: '/siteadmin/messages', privilegeId: 9 },
          { type: 'item', icon: Share2, label: 'Social Media', path: '/siteadmin/social-media' },
          { type: 'item', icon: BarChart3, label: 'Social Analytics', path: '/siteadmin/social-analytics' },
        ]
      },
      {
        title: 'CONTENIDO',
        items: [
          {
            type: 'submenu',
            icon: Home,
            label: formatMessage(messages.homePageSettings),
            menuKey: 'homePage',
            activeCheck: this.isActive(['/siteadmin/home/', '/siteadmin/popularlocation', '/siteadmin/smart-', '/siteadmin/card-style']),
            privilegeId: 15,
            items: [
              { label: formatMessage(messages.homeModulesLabel), path: '/siteadmin/home/modules' },
              { label: formatMessage(messages.bannerCaptionLabel), path: '/siteadmin/home/caption' },
              { label: formatMessage(messages.bannerCaptionLabel2), path: '/siteadmin/home/marketing' },
              { label: formatMessage(messages.adminLanguages), path: '/siteadmin/home/languages' },
              { label: formatMessage(messages.smartTranslations), path: '/siteadmin/smart-translations' },
              { label: formatMessage(messages.smartEmailTemplates), path: '/siteadmin/smart-email-templates' },
              { label: formatMessage(messages.cardStyleSettings), path: '/siteadmin/card-style' },
              { label: formatMessage(messages.findYourCar), path: '/siteadmin/home/find-your-boat' },
              { label: formatMessage(messages.managePopularLocations), path: '/siteadmin/popularlocation' },
              { label: 'Map Section', path: '/siteadmin/home/map-section' },
              { label: formatMessage(messages.imageBannerLabel), path: '/siteadmin/home/banner' },
              { label: formatMessage(messages.staticInfoBlock), path: '/siteadmin/home/static-info-block' },
              { label: formatMessage(messages.footerBlockLabel), path: '/siteadmin/home/footer-block' },
              { label: formatMessage(messages.cancellationPolicies), path: '/siteadmin/home/cancellation-policies' },
            ]
          },
          {
            type: 'submenu',
            icon: FileText,
            label: formatMessage(messages.whyBecomeOwnerPage),
            menuKey: 'whyHost',
            activeCheck: this.isActive('/siteadmin/whyHost/'),
            privilegeId: 16,
            items: [
              { label: 'Block 1', path: '/siteadmin/whyHost/Block1' },
              { label: 'Block 2', path: '/siteadmin/whyHost/Block2' },
              { label: 'Block 3', path: '/siteadmin/whyHost/Block3' },
              { label: 'Block 4', path: '/siteadmin/whyHost/Block4' },
              { label: 'Block 5', path: '/siteadmin/whyHost/Block5' },
              { label: 'Block 6 (Reviews)', path: '/siteadmin/whyHost/review' },
              { label: 'Block 7', path: '/siteadmin/whyHost/Block7' },
            ]
          },
          { type: 'item', icon: FileText, label: formatMessage(messages.whyHostPage), path: '/siteadmin/why-host', privilegeId: 16 },
          { type: 'item', icon: BookOpen, label: formatMessage(messages.contentManagementLabel), path: '/siteadmin/content-management', privilegeId: 18 },
          { type: 'item', icon: FileText, label: formatMessage(messages.staticContentManagement), path: '/siteadmin/staticpage/management', privilegeId: 19 },
          { type: 'item', icon: Globe, label: 'Content Management (i18n)', path: '/siteadmin/content-management-unified', privilegeId: 19 },
          { type: 'item', icon: Search, label: formatMessage(messages.searchSettings), path: '/siteadmin/settings/search', privilegeId: 14 },
        ]
      },
      {
        title: 'APP MÓVIL',
        items: [
          {
            type: 'submenu',
            icon: Smartphone,
            label: 'App Config',
            menuKey: 'appConfig',
            activeCheck: this.isActive(['/siteadmin/notifications', '/siteadmin/flutter-config', '/siteadmin/chat-filter-messages', '/siteadmin/app-ads']),
            items: [
              { label: 'Gestor de Notificaciones', path: '/siteadmin/notifications', privilegeId: 9 },
              { label: 'Flutter Remote Config', path: '/siteadmin/flutter-config', privilegeId: 15 },
              { label: 'Mensajes del Chat', path: '/siteadmin/chat-filter-messages', privilegeId: 15 },
              { label: 'Publicidad (Ads)', path: '/siteadmin/app-ads', privilegeId: 15 },
            ]
          }
        ]
      },
      {
        title: 'BOLETIN',
        items: [
          { type: 'item', icon: Mail, label: 'Boletin General', path: '/siteadmin/newsletter?tab=general' },
          { type: 'item', icon: UserCog, label: 'Comunicados Propietarios', path: '/siteadmin/newsletter?tab=hosts' },
        ]
      },
      {
        title: 'CUENTA',
        items: [
          { type: 'item', icon: KeyRound, label: formatMessage(messages.changePasswordLabel), path: '/siteadmin/change/admin', superAdminOnly: true },
        ]
      },
    ];

    return (
      <div className="adminCollapse">
        {/* Mobile Header */}
        <div className={s.mobileHeader}>
          <div onClick={this.openMenu} className={s.hamburger}>
            <span className="icon-bar"></span>
            <span className="icon-bar"></span>
            <span className="icon-bar"></span>
          </div>
          <Logo />
          <button onClick={() => adminLogout()} className={s.logoutBtn}>
            <LogOut size={20} />
          </button>
        </div>

        {/* Sidebar Container */}
        <div className={cx(
          s.sidebarContainer,
          { [s.collapsed]: isCollapsed },
          { [s.menuOpen]: isOpen === 1 }
        )}>
          <div className={s.sidebarNavParent}>
            {/* Close button for mobile */}
            <div className={s.closeColor} onClick={this.closeMenu}>
              <X size={24} />
            </div>

            {/* Logo Section */}
            <div className={s.sideBarWelcome}>
              <Logo />
              <button className={s.collapseToggle} onClick={this.toggleCollapse}>
                {isCollapsed ? <PanelLeft size={20} /> : <PanelLeftClose size={20} />}
              </button>
            </div>

            {/* Navigation */}
            <ul className={s.sidebarNav} ref={this.sidebarNavRef}>
              {/* Language Selector (Mobile) */}
              <li className={s.tabViewSection}>
                <button
                  className={s.sideNavitem}
                  onClick={() => openHeaderModal('languageModal')}
                >
                  <Languages className={s.sideNavIcon} size={20} />
                  <span className={s.menuText}>{formatLocale(currentLocale)}</span>
                </button>
              </li>

              {/* Go to Main Site (Mobile) */}
              <li className={s.tabViewSection}>
                <Link to="/" className={s.sideNavitem}>
                  <ExternalLink className={s.sideNavIcon} size={20} />
                  <span className={s.menuText}>
                    <FormattedMessage {...messages.GotoMainSite} />
                  </span>
                </Link>
              </li>

              {/* Menu Groups */}
              {menuGroups.map((group, groupIdx) => {
                // Filter items based on privileges
                const visibleItems = group.items.filter(item => {
                  if (item.privilegeId) {
                    return validatePrivilege(item.privilegeId, privileges) || isSuperAdmin;
                  }
                  if (item.superAdminOnly) {
                    return isSuperAdmin;
                  }
                  return true;
                });

                if (visibleItems.length === 0) return null;

                return (
                  <React.Fragment key={groupIdx}>
                    <li className={s.groupHeader}>{group.title}</li>
                    {visibleItems.map((item, itemIdx) => {
                      if (item.type === 'item') {
                        return (
                          <React.Fragment key={item.path || itemIdx}>
                            {this.renderMenuItem(
                              item.icon,
                              item.label,
                              item.path,
                              item.privilegeId
                            )}
                          </React.Fragment>
                        );
                      }
                      if (item.type === 'submenu') {
                        return (
                          <React.Fragment key={item.menuKey || itemIdx}>
                            {this.renderSubmenu(
                              item.icon,
                              item.label,
                              item.menuKey,
                              item.items,
                              item.activeCheck
                            )}
                          </React.Fragment>
                        );
                      }
                      return null;
                    })}
                  </React.Fragment>
                );
              })}
            </ul>

            {/* User Profile Section */}
            <div className={s.userSection}>
              <div className={s.userProfile}>
                <div className={s.userAvatar}>
                  {isSuperAdmin ? 'SA' : 'A'}
                </div>
                <div className={s.userInfo}>
                  <p className={s.userName}>
                    {isSuperAdmin ? 'Super Admin' : 'Admin'}
                  </p>
                  <p className={s.userRole}>
                    {isSuperAdmin ? 'Full Access' : 'Limited Access'}
                  </p>
                </div>
                <button onClick={() => adminLogout()} className={s.logoutBtn} title="Cerrar sesión">
                  <LogOut size={18} />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

const mapState = (state) => ({
  currentLocale: state.intl.locale,
  isSuperAdmin: state.runtime.isSuperAdmin,
  privileges: state.account.privileges?.privileges || [],
});

const mapDispatch = {
  adminLogout,
  openHeaderModal
};

export default injectIntl(withStyles(s)(connect(mapState, mapDispatch)(SideBar)));
